import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { dispatchApi as D, DISPATCH_STATUS_STYLE } from "@/lib/dispatch";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Search, Loader2, Plus } from "lucide-react";

export default function Dispatch() {
  const { can } = useAuth();
  const navigate = useNavigate();
  const [meta, setMeta] = useState({ statuses: [] });
  const [stats, setStats] = useState({});
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [open, setOpen] = useState(false);
  const [sos, setSos] = useState([]);
  const [soNo, setSoNo] = useState("");
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState([]);
  const canCreate = can("dispatch", "create");

  const load = async () => {
    setLoading(true);
    try { const [s, list] = await Promise.all([D.stats(), D.list({ search: search || undefined, status })]); setStats(s); setRows(list); }
    finally { setLoading(false); }
  };
  useEffect(() => { D.meta().then(setMeta); }, []);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);
  useEffect(() => { const t = setTimeout(load, 350); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [search]);

  const openNew = async () => { setSos(await D.salesOrders()); setSoNo(""); setItems([]); setSelected([]); setOpen(true); };
  const onPickSo = async (v) => { setSoNo(v); setSelected([]); setItems(await D.reservable(v)); };
  const toggle = (id) => setSelected((p) => p.includes(id) ? p.filter((x) => x !== id) : [...p, id]);
  const create = async () => {
    if (selected.length === 0) { toast.error("Select at least one roll"); return; }
    try { const r = await D.create({ item_ids: selected }); toast.success(`Challan ${r.challan_no} created`); setOpen(false); navigate(`/dispatch/${r.id}`); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  return (
    <div className="space-y-6" data-testid="dispatch-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-heading text-2xl font-bold tracking-tight text-slate-900">Dispatch</h2>
          <p className="mt-1 text-sm text-slate-500">Outbound shipments. Only reserved inventory ships; deduction happens on confirmed dispatch.</p>
        </div>
        {canCreate && <Button onClick={openNew} data-testid="new-challan-btn" className="bg-indigo-600 text-white hover:bg-indigo-700"><Plus className="mr-2 h-4 w-4" />New Challan</Button>}
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Card className="p-4" data-testid="dsp-stat-inprogress"><p className="text-xs uppercase tracking-wider text-slate-500">In progress</p><p className="mt-1 font-heading text-2xl font-bold text-blue-600">{stats.in_progress ?? 0}</p></Card>
        <Card className="p-4" data-testid="dsp-stat-dispatched"><p className="text-xs uppercase tracking-wider text-slate-500">Dispatched</p><p className="mt-1 font-heading text-2xl font-bold text-indigo-600">{stats.dispatched ?? 0}</p></Card>
        <Card className="p-4" data-testid="dsp-stat-meters"><p className="text-xs uppercase tracking-wider text-slate-500">Dispatched meters</p><p className="mt-1 font-heading text-2xl font-bold text-slate-900">{(stats.dispatched_meters ?? 0).toLocaleString("en-IN")}</p></Card>
        <Card className="p-4" data-testid="dsp-stat-orders"><p className="text-xs uppercase tracking-wider text-slate-500">Orders to ship</p><p className="mt-1 font-heading text-2xl font-bold text-amber-600">{stats.dispatchable_orders ?? 0}</p></Card>
      </div>

      <Card className="p-4">
        <div className="mb-4 flex flex-wrap gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search challan / SO / customer" className="pl-9" data-testid="dsp-search-input" />
          </div>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-48" data-testid="dsp-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">All statuses</SelectItem>{meta.statuses.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        {loading ? <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-indigo-600" /></div> : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader><TableRow className="bg-slate-50">
                <TableHead className="text-xs uppercase text-slate-500">Challan</TableHead>
                <TableHead className="text-xs uppercase text-slate-500">Customer</TableHead>
                <TableHead className="text-xs uppercase text-slate-500">Rolls</TableHead>
                <TableHead className="text-xs uppercase text-slate-500">Vehicle</TableHead>
                <TableHead className="text-xs uppercase text-slate-500">Status</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.id} className="cursor-pointer" onClick={() => navigate(`/dispatch/${r.id}`)} data-testid={`dispatch-row-${r.challan_no}`}>
                    <TableCell className="font-medium text-slate-800">{r.challan_no}<span className="block text-xs text-slate-400">{r.so_no}</span></TableCell>
                    <TableCell className="text-slate-700">{r.customer}</TableCell>
                    <TableCell className="text-slate-600">{(r.items || []).length}</TableCell>
                    <TableCell className="text-slate-600">{r.transporter?.vehicle_no || "—"}</TableCell>
                    <TableCell><Badge className={`hover:bg-inherit ${DISPATCH_STATUS_STYLE[r.status]}`}>{r.status}</Badge></TableCell>
                  </TableRow>
                ))}
                {rows.length === 0 && <TableRow><TableCell colSpan={5} className="py-12 text-center text-slate-400" data-testid="dispatch-empty">No challans yet. Create one from an order with reserved stock.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader><DialogTitle>New Delivery Challan</DialogTitle><DialogDescription>Pick a Sales Order, then select its reserved rolls to ship.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label>Sales Order</Label>
              <Select value={soNo} onValueChange={onPickSo}>
                <SelectTrigger data-testid="challan-so-select"><SelectValue placeholder="Select an order with reserved stock" /></SelectTrigger>
                <SelectContent>{sos.map((s) => <SelectItem key={s.so_no} value={s.so_no}>{s.so_no} · {s.customer} ({s.reserved_items} reserved)</SelectItem>)}</SelectContent>
              </Select>
              {sos.length === 0 && <p className="text-xs text-amber-600">No orders have reserved stock. Reserve inventory first.</p>}
            </div>
            {soNo && (
              <div className="max-h-64 space-y-1 overflow-y-auto rounded-md border border-slate-200 p-2">
                {items.map((it) => (
                  <label key={it.id} className="flex items-center justify-between rounded px-2 py-1.5 hover:bg-slate-50" data-testid={`reservable-${it.item_no}`}>
                    <span className="flex items-center gap-2 text-sm"><Checkbox checked={selected.includes(it.id)} onCheckedChange={() => toggle(it.id)} data-testid={`select-${it.item_no}`} />{it.item_no} · {it.batch_id}</span>
                    <span className="text-sm text-slate-500">{it.meters} m · {it.qc_grade || "—"}</span>
                  </label>
                ))}
                {items.length === 0 && <p className="py-3 text-center text-sm text-slate-400">No reserved rolls for this order.</p>}
              </div>
            )}
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={create} disabled={selected.length === 0} data-testid="create-challan-btn" className="bg-indigo-600 text-white hover:bg-indigo-700">Create ({selected.length})</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
